import { useMemo, useState } from "react";
import { REDUNDANCY, TAX_YEAR, gbp, redundancyRateForAge } from "@/lib/statutory";

export function RedundancyCalculator() {
  const [age, setAge] = useState(35);
  const [years, setYears] = useState(5);
  const [weeklyPay, setWeeklyPay] = useState(550);

  const { weeks, cappedPay, total, breakdown } = useMemo(() => {
    const cappedYears = Math.min(years, REDUNDANCY.serviceYearsCap);
    const cappedPay = Math.min(weeklyPay, REDUNDANCY.weeklyPayCap);
    let weeks = 0;
    const rows: { ageBand: string; rate: number; year: number }[] = [];
    for (let i = 0; i < cappedYears; i++) {
      const ageDuringYear = age - i - 1;
      const rate = redundancyRateForAge(ageDuringYear);
      weeks += rate;
      rows.push({
        ageBand:
          ageDuringYear >= 41 ? "41+" : ageDuringYear >= 22 ? "22–40" : "Under 22",
        rate,
        year: i + 1,
      });
    }
    return { weeks, cappedPay, total: weeks * cappedPay, breakdown: rows };
  }, [age, years, weeklyPay]);

  return (
    <div className="rounded-3xl border border-border bg-card p-8 shadow-sm sm:p-10">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
        <NumberField label="Employee age" value={age} onChange={setAge} min={16} max={75} />
        <NumberField label="Full years of service" value={years} onChange={setYears} min={0} max={50} />
        <NumberField label="Gross weekly pay (£)" value={weeklyPay} onChange={setWeeklyPay} min={0} step={10} />
      </div>

      <div className="mt-8 rounded-2xl bg-brand p-6 text-brand-foreground">
        <p className="text-xs font-semibold uppercase tracking-widest text-brand-foreground/60">Statutory redundancy pay</p>
        <p className="mt-3 font-serif text-5xl">{gbp(total, { decimals: 2 })}</p>
        <p className="mt-3 text-sm text-brand-foreground/70">
          {weeks} weeks × {gbp(cappedPay)} weekly pay (capped at {gbp(REDUNDANCY.weeklyPayCap)}).
        </p>
      </div>

      <div className="mt-6 rounded-xl border border-border bg-background p-5">
        <h3 className="cursor-pointer text-sm font-medium text-brand">How this is calculated</h3>
        <div className="mt-4 space-y-3 text-sm text-muted-foreground">
          <p>
            UK statutory redundancy pay is based on age during each year of continuous service, capped at{" "}
            {REDUNDANCY.serviceYearsCap} years and at a statutory weekly pay cap of {gbp(REDUNDANCY.weeklyPayCap)} ({TAX_YEAR}).
          </p>
          <ul className="ml-5 list-disc space-y-1">
            <li>{REDUNDANCY.ratePerYear.under22} week's pay for each full year under age 22</li>
            <li>{REDUNDANCY.ratePerYear.age22to40} week's pay for each full year aged 22–40</li>
            <li>{REDUNDANCY.ratePerYear.age41plus} weeks' pay for each full year aged 41+</li>
          </ul>
          <p className="mt-2">
            Employees with under {REDUNDANCY.qualifyingYears} years' continuous service are not entitled to statutory
            redundancy pay. Enhanced contractual schemes may give more.
          </p>
        </div>
      </div>

      <div className="mt-3 rounded-xl border border-border bg-background p-5">
        <h3 className="cursor-pointer text-sm font-medium text-brand">Year-by-year breakdown</h3>
        <table className="mt-4 w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="pb-2">Year</th>
              <th className="pb-2">Age band</th>
              <th className="pb-2">Weeks</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {breakdown.map((r) => (
              <tr key={r.year}>
                <td className="py-2">{r.year}</td>
                <td className="py-2">{r.ageBand}</td>
                <td className="py-2">{r.rate}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function NumberField({
  label,
  value,
  onChange,
  min,
  max,
  step,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
  min?: number;
  max?: number;
  step?: number;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-foreground">{label}</span>
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange(Number(e.target.value || 0))}
        className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-base focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
      />
    </label>
  );
}
